import { getSessionForm } from "../../../../../../../../../shared/api/form";
import type {
	IAdGroup,
	IConversionCatalogItem,
	ISessionFormApiPayload,
	ISessionFormApiCampaignSnapshot,
} from "../../../../../../../../../shared/types/form.type";
import { coerceStringArray } from "../utils/campaign-form-model";
import { fetchConversionActions } from "./get-conversion-actions.service";

// Приводит скалярное поле снимка к строке (числа — к строке, null/undefined — пропускается).
const toStringOrNull = (value: unknown): string | null => {
	if (typeof value === "string") return value;
	if (typeof value === "number" && Number.isFinite(value))
		return String(value);
	return null;
};

// Делитель миксов Google Ads (совпадает с normalize_form бэкенда).
const MICROS_DIVISOR = 1_000_000;

// Денежные поля (budget.amount_micros, campaign.target_cpa_micros) приходят в двух видах:
// WS campaign_form.update шлёт RAW-форму HI Service с миксами (250000000),
// GET /session/:id/form — уже нормализованные деньги (250.0). Эвристика та же, что в
// normalize_form бэкенда: число >= 1_000_000 считается миксами и переводится в валюту.
const toCurrencyStringOrNull = (value: unknown): string | null => {
	let num: number;
	if (typeof value === "number") {
		num = value;
	} else if (typeof value === "string" && value.trim() !== "") {
		const parsed = Number(value.replace(",", "."));
		if (!Number.isFinite(parsed)) return null;
		num = parsed;
	} else {
		return null;
	}
	if (!Number.isFinite(num)) return null;
	const currency =
		num >= MICROS_DIVISOR
			? Math.round((num / MICROS_DIVISOR) * 100) / 100
			: num;
	return String(currency);
};

// Санитизирует объект campaign из бэкенда: строки/числа -> строки, null отбрасывается,
// массивы (geo/language/conversions) приводятся к string[]. Бэкенд шлёт null для
// незаполненных полей (pydantic None) и числа после нормализации микросов.
const sanitizeCampaignSnapshot = (
	campaign: Record<string, unknown>,
): ISessionFormApiCampaignSnapshot => {
	const out: ISessionFormApiCampaignSnapshot = {};
	const copyScalar = (key: keyof ISessionFormApiCampaignSnapshot) => {
		const coerced = toStringOrNull(campaign[key as string]);
		if (coerced !== null)
			(out as Record<string, unknown>)[key as string] = coerced;
	};
	copyScalar("name");
	copyScalar("status");
	copyScalar("app_store");
	copyScalar("app_id");
	// Денежное поле: миксы из raw WS-формы → валюта (см. toCurrencyStringOrNull)
	const targetCpa = toCurrencyStringOrNull(campaign.target_cpa_micros);
	if (targetCpa !== null) out.target_cpa_micros = targetCpa;
	copyScalar("start_date");
	copyScalar("end_date");
	if (Array.isArray(campaign.selective_optimization_conversion_actions)) {
		out.selective_optimization_conversion_actions =
			coerceStringArray(campaign.selective_optimization_conversion_actions);
	}
	if (Array.isArray(campaign.geo_targets)) {
		out.geo_targets = coerceStringArray(campaign.geo_targets);
	}
	if (Array.isArray(campaign.language_targets)) {
		out.language_targets = coerceStringArray(campaign.language_targets);
	}
	return out;
};

// Разбирает плоский объект формы (без обёрток form/data).
// requireCustomerId: строгий режим GET-эндпоинта (customer_id обязателен);
// для WS-обновлений customer_id может отсутствовать — подставляем "".
const parseFlatSessionFormPayload = (
	rawPayload: Record<string, unknown>,
	requireCustomerId: boolean,
): ISessionFormApiPayload | null => {
	if (requireCustomerId && typeof rawPayload.customer_id !== "string")
		return null;

	const account_name =
		typeof rawPayload.account_name === "string" ? rawPayload.account_name : "";
	const campaign =
		rawPayload.campaign && typeof rawPayload.campaign === "object"
			? sanitizeCampaignSnapshot(
					rawPayload.campaign as Record<string, unknown>,
				)
			: {};
	const adgroups = Array.isArray(rawPayload.adgroups)
		? (rawPayload.adgroups as IAdGroup[])
		: [];
	let budget: ISessionFormApiPayload["budget"];
	if (rawPayload.budget && typeof rawPayload.budget === "object") {
		const budgetRecord = rawPayload.budget as Record<string, unknown>;
		// Денежное поле: миксы из raw WS-формы → валюта (см. toCurrencyStringOrNull)
		const amountMicros = toCurrencyStringOrNull(budgetRecord.amount_micros);
		if (amountMicros !== null) {
			budget = { amount_micros: amountMicros };
		}
	}
	const start_date = toStringOrNull(rawPayload.start_date);
	const end_date = toStringOrNull(rawPayload.end_date);
	return {
		customer_id:
			typeof rawPayload.customer_id === "string"
				? rawPayload.customer_id
				: "",
		account_name,
		adgroups,
		campaign: campaign ?? {},
		...(budget ? { budget } : {}),
		...(start_date !== null ? { start_date } : {}),
		...(end_date !== null ? { end_date } : {}),
		...(typeof rawPayload.type === "string" ? { type: rawPayload.type } : {}),
		...(typeof rawPayload.action === "string"
			? { action: rawPayload.action }
			: {}),
	};
};

// Разбирает сырое тело ответа API в структуру формы сессии.
// Поддерживает: вложенный `form`, вложенный `data`, либо плоский объект как в MSW.
const parseSessionFormResponseBody = (
	data: unknown,
): ISessionFormApiPayload | null => {
	if (!data || typeof data !== "object") return null;
	const rawPayload = data as Record<string, unknown>;

	// Реальный бэкенд: { success, session_id, form: { customer_id, campaign, ... } }
	const wrappedForm = rawPayload.form;
	if (wrappedForm && typeof wrappedForm === "object") {
		const parsed = parseSessionFormResponseBody(wrappedForm);
		if (parsed) return parsed;
	}

	const nestedData = rawPayload.data;
	if (nestedData && typeof nestedData === "object") {
		const parsed = parseSessionFormResponseBody(nestedData);
		if (parsed) return parsed;
	}

	return parseFlatSessionFormPayload(rawPayload, true);
};

// Разбирает payload WS-события campaign_form.update ({ ok, campaign_context })
// в снимок формы. Отличается от GET-разбора: customer_id не обязателен
// (аккаунт уже выбран в сессии), поля нормализуются так же.
export const parseCampaignFormUpdatePayload = (
	data: unknown,
): ISessionFormApiPayload | null => {
	if (!data || typeof data !== "object") return null;
	const campaignContext = (data as Record<string, unknown>).campaign_context;
	if (!campaignContext || typeof campaignContext !== "object") return null;
	return parseFlatSessionFormPayload(
		campaignContext as Record<string, unknown>,
		false,
	);
};

// Запрашивает снимок формы сессии с API. Возвращает null при ошибке, отсутствии данных
// или если за время запроса сессия перестала быть актуальной (isSessionStillActive).
export const fetchSessionFormPayload = async (
	sessionId: string,
	userId: string,
	isSessionStillActive: () => boolean,
): Promise<ISessionFormApiPayload | null> => {
	const sessionFormResponse = await getSessionForm({
		session_id: sessionId,
		user_id: userId,
	});
	if (
		!isSessionStillActive() ||
		!sessionFormResponse.success ||
		sessionFormResponse.data == null
	)
		return null;
	return parseSessionFormResponseBody(sessionFormResponse.data);
};

// Загружает каталог конверсий для указанного приложения (app_id) и customer id аккаунта.
// Учитывает актуальность сессии; при пустых идентификаторах или ошибке ответа возвращает [].
export const fetchConversionsCatalog = async (params: {
	appIdTrimmed: string;
	buyerId: number;
	accountIdFormattedForApi: string;
	isSessionStillActive: () => boolean;
}): Promise<IConversionCatalogItem[]> => {
	const {
		appIdTrimmed,
		buyerId,
		accountIdFormattedForApi,
		isSessionStillActive,
	} = params;
	if (!appIdTrimmed || !accountIdFormattedForApi) return [];
	const result = await fetchConversionActions({
		app_id: appIdTrimmed,
		buyer_id: buyerId,
		customer_id: accountIdFormattedForApi.replace(/-/g, ""),
	});
	if (!isSessionStillActive()) return [];
	if (result.data?.ok && Array.isArray(result.data.conversions)) {
		return result.data.conversions.map((conversion) => ({
			key: conversion.resource_name,
			name: conversion.name,
			// category/type (enum names Google Ads) нужны pruning-эффекту для
			// подстановки семантических goal names черновика ("first_open")
			category: conversion.category || undefined,
			type: conversion.type || undefined,
		}));
	}
	return [];
};
